import { useState, useRef, ChangeEvent } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	Typography,
	IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TUploadVersionDialogProps, TUploadVersionFormData } from './types';

const validationSchema = yup.object({
	file: yup.mixed().required('File is required'),
	comment: yup.string(),
});

export const UploadVersionDialog = ({
	open,
	attachmentId,
	onClose,
	onSave,
}: TUploadVersionDialogProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const formik = useFormik<TUploadVersionFormData>({
		initialValues: {
			file: null,
			comment: '',
		},
		validationSchema,
		onSubmit: values => {
			if (!attachmentId) return;
			onSave(values);
			formik.resetForm();
			setSelectedFile(null);
			onClose();
		},
	});

	const handleClose = () => {
		formik.resetForm();
		setSelectedFile(null);
		onClose();
	};

	const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		setSelectedFile(file);
		formik.setFieldValue('file', file).catch(() => {});
	};

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
		formik.setFieldValue('file', null).catch(() => {});
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '500px',
					maxWidth: '500px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Upload New Version</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent sx={{ p: '12px 24px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{/* File Upload Section */}
						<Box>
							<input
								type="file"
								ref={fileInputRef}
								onChange={handleFileSelect}
								style={{ display: 'none' }}
							/>
							{!selectedFile ? (
								<Box
									onClick={handleUploadClick}
									sx={{
										border: '2px dashed',
										borderColor:
											formik.touched.file && formik.errors.file ? 'error.main' : 'primary.main',
										borderRadius: '8px',
										p: 3,
										textAlign: 'center',
										cursor: 'pointer',
										'&:hover': {
											backgroundColor: 'action.hover',
										},
									}}
								>
									<CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
									<Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
										Click to upload file
									</Typography>
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										border: '1px solid',
										borderColor: 'divider',
										borderRadius: '8px',
										p: 2,
									}}
								>
									<Typography variant="body2" noWrap sx={{ flex: 1 }}>
										{selectedFile.name}
									</Typography>
									<IconButton size="small" onClick={handleRemoveFile}>
										<CloseIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{formik.touched.file && formik.errors.file && (
								<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
									{String(formik.errors.file)}
								</Typography>
							)}
						</Box>

						{/* Comment Field */}
						<TextField
							id="comment"
							name="comment"
							label="Comment"
							size="small"
							fullWidth
							multiline
							rows={3}
							value={formik.values.comment}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.comment && Boolean(formik.errors.comment)}
							helperText={formik.touched.comment && formik.errors.comment}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button type="submit" variant="contained">
						Upload
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
